export interface OKRPermissions {
    isOwner: boolean;
    currentUserId: string;
    currentUserName: string;
    /** Only owner sees financial KPI values (€, CA, coûts). */
    canSeeFinancials: boolean;
    /** Only owner sees the full team performance panel. */
    canSeeTeamDetails: boolean;
    /** Only owner can create/edit/delete objectives, KPIs, action plans. */
    canManageObjectives: boolean;
    /** Only owner sees the Templates and Weekly Review management. */
    canSeeOwnerFeatures: boolean;
    /** Everyone can assign tasks to anyone (coordination). */
    canAssignToOthers: true;
    /** Sub-users default to their own tasks view. */
    defaultToOwnTasks: boolean;
}

export function useOKRPermissions(): OKRPermissions {
    const ownerId = localStorage.getItem('ownerId');
    const isOwner = !ownerId;
    const currentUserId = localStorage.getItem('userId') ?? '';
    const currentUserName = localStorage.getItem('name') ?? '';
    const permissions: string[] = (() => {
        try { return JSON.parse(localStorage.getItem('permissions') ?? '[]'); } catch { return []; }
    })();

    const hasFullPermission = isOwner || permissions.includes('all') || permissions.includes('show:number-dashboard');
    // Owner always has this implicitly; `manage:okr` lets the owner delegate
    // objective/KPI/alert management to a trusted sub-account (see migration
    // 021-addManageOkrPermission) instead of it being all-or-nothing on isOwner.
    const canManageOkr = isOwner || permissions.includes('all') || permissions.includes('manage:okr');

    return {
        isOwner,
        currentUserId,
        currentUserName,
        canSeeFinancials: hasFullPermission,
        canSeeTeamDetails: canManageOkr,
        canManageObjectives: canManageOkr,  // owner, or a sub-account granted manage:okr, can edit/delete objectives & KPIs
        canSeeOwnerFeatures: canManageOkr,  // templates, weekly review lock
        canAssignToOthers: true,
        defaultToOwnTasks: !canManageOkr,
    };
}

/** Returns true if a KPI value should be masked (€ or revenue metric). */
export function isFinancialKPI(unit: string, metricKey?: string): boolean {
    if (unit === '€') return true;
    const financialKeys = ['revenue', 'ca', 'cost', 'avg_order_cost', 'pipeline_value', 'avg_payment_delay', 'invoices_pending'];
    return financialKeys.some((k) => (metricKey ?? '').includes(k));
}
